import {Dispatch, FC, SetStateAction, useState} from 'react';
import {ReactElement} from 'react';

import {Box, Divider, IconButton, Typography, useMediaQuery} from '@mui/material';
import {When, If, Else, Then} from 'react-if';
import {useDispatch} from 'react-redux';

import ChevronLeftIcon from 'assets/icons/chevronLeft';
import CloseOutlinedIcon from 'assets/icons/closedOutlinedIcon';
import DeleteOutlineIcon2 from 'assets/icons/deleteOutlineIcon2';
import {ExpressIcon} from 'assets/icons/expressIcon';
import theme from 'config/theme';
import {useAppSelector} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {setVariantIdsOfItemThatWasAdjustedQuantityAction} from 'store/actions/basket-action';
import {UserAddress} from 'types';
import {selectedItemsLocalStorage} from 'utils/LocalStorage';

import {StyledHeaderWrapper, StyledHeaderContainer, StyledBasketDrawerHeader} from './styles';
import BasketAppHeader from '../../components/app-header-container/BasketAppHeader';
import {setShowBuildInfo} from '../../store/actions/app-action';
import {ShippingAddress} from '../shipping-address/ShippingAddress';

export type HeaderProps = {
  children?: ReactElement;
  address?: UserAddress;
  isLoadingAddress: boolean;
  onShipToClick?: () => void;
  title: string;
  setShowConfirmModal: Dispatch<SetStateAction<boolean>>;
};

export const Header: FC<HeaderProps> = ({
  children,
  address,
  isLoadingAddress,
  onShipToClick,
  title,
  setShowConfirmModal
}) => {
  const session = useAppSelector((state) => state?.auth?.idToken);
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const {isExpress, isRegular} = useAppMode();
  const {basket} = useAppSelector((state) => state?.basket);
  const isAuthenticated = !!session;
  const dispatch = useDispatch();
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const isMobileScreen = isReactNativeView || isMobileView;

  const selectedItems = selectedItemsLocalStorage.get();
  const basketCount = (basket?.basketItems?.length || 0) + (basket?.unavailableItems.length || 0);
  const onExit = () => {
    SendBrowserEvent.closeBasketDrawer();
    // hide all quantity adjust labels when close because we need to show it only once
    dispatch(setVariantIdsOfItemThatWasAdjustedQuantityAction([]));
  };

  const ExitBasket = () => (
    <IconButton data-test-delete-btn="" type="button" size="small" onClick={onExit}>
      <ChevronLeftIcon width={24} height={24} />
    </IconButton>
  );

  const BasketTitle = (props?: {fontSize?: string; fontWeight?: string}) => {
    const [cheat, setCheat] = useState(1);
    return (
      <Typography
        onClick={() => {
          cheat === 10 && dispatch(setShowBuildInfo(true));
          setCheat((cheat) => cheat + 1);
        }}
        component={'h4'}
        variant="h4"
        id="lbl_title_basket_page"
        fontSize={props?.fontSize}
        fontWeight={props?.fontWeight}
      >
        {title}
      </Typography>
    );
  };

  const BasketItemCount = (props?: {fontSize?: string; fontWeight?: string}) => (
    <Typography component={'h4'} variant="h4" fontSize={props?.fontSize} fontWeight={props?.fontWeight}>
      {`(${basketCount})`}
    </Typography>
  );

  const DeleteSelectedItemsButton = (props?: {height?: string; width?: string; color?: string}) => (
    <IconButton data-test-delete-btn="" type="button" onClick={() => setShowConfirmModal(true)} size="small">
      <DeleteOutlineIcon2 fillColor={props?.color} fontSize="small" height={props?.height} width={props?.width} />
    </IconButton>
  );

  return (
    <StyledHeaderWrapper>
      <StyledHeaderContainer isReactNativeView={isMobileScreen}>
        <If condition={isMobileScreen}>
          <Then>
            <BasketAppHeader
              leadingElement={<ExitBasket />}
              headerTitle={
                <>
                  <When condition={isExpress}>
                    <ExpressIcon width={20} height={20} />
                  </When>
                  <BasketTitle fontSize="20px" fontWeight="700" />
                  <When condition={basketCount}>
                    <BasketItemCount fontSize="20px" fontWeight="700" />
                  </When>
                </>
              }
              trailingElement={
                <When condition={selectedItems?.length && basket?.basketItems?.length}>
                  <DeleteSelectedItemsButton color="#C7C7C7" height={'24px'} width={'24px'} />
                </When>
              }
            />
          </Then>
          <Else>
            <StyledBasketDrawerHeader>
              <When condition={isExpress}>
                <ExitBasket />
              </When>
              <Box display="flex" alignItems="center" justifyContent="center" gap="5px">
                <When condition={isExpress}>
                  <ExpressIcon width={20} height={20} />
                </When>
                <BasketTitle />
                <When condition={basketCount}>
                  <BasketItemCount fontWeight="400" />
                </When>
              </Box>
              <Box>
                <When condition={selectedItems?.length && basket?.basketItems?.length}>
                  <DeleteSelectedItemsButton color="#212124" height={'20px'} width={'20px'} />
                </When>
                <When condition={isRegular}>
                  <IconButton data-test-delete-btn="" type="button" size="small" onClick={onExit}>
                    <CloseOutlinedIcon color="secondary" fontSize="medium" />
                  </IconButton>
                </When>
              </Box>
            </StyledBasketDrawerHeader>
          </Else>
        </If>
        {isAuthenticated && !isMobileScreen ? (
          <ShippingAddress address={address as UserAddress} isLoading={isLoadingAddress} onClick={onShipToClick} />
        ) : (
          <></>
        )}
      </StyledHeaderContainer>

      <Divider />

      {children}
    </StyledHeaderWrapper>
  );
};

export default Header;
